/* ── CompareView ─────────────────────────────────────────────────
   D1 Redesign: Two-column card layout for edition/jurisdiction
   comparison. Left card = Standard A (AU), Right card = Standard B
   (international equivalent). Each column scrolls independently.
   Differences are highlighted with the accent colour.
   ────────────────────────────────────────────────────────────── */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CompareClausePair, CompareResponse, EditionInfo } from '../api';
import { fetchCompare, fetchEditions } from '../api';

/* ── Diff helpers ──────────────────────────────────────────── */

/** Simple word-level diff for highlighting changes */
function diffWords(
  oldText: string | null,
  newText: string | null,
): { old: { text: string; type: 'same' | 'removed' }[]; new: { text: string; type: 'same' | 'added' }[] } {
  if (!oldText && !newText) return { old: [], new: [] };
  if (!oldText) return { old: [], new: [{ text: newText!, type: 'added' }] };
  if (!newText) return { old: [{ text: oldText!, type: 'removed' }], new: [] };

  const wordsA = oldText.split(/(\s+)/);
  const wordsB = newText.split(/(\s+)/);

  // Simple LCS-based approach; for very long texts just mark as changed
  if (wordsA.length > 500 || wordsB.length > 500) {
    return {
      old: [{ text: oldText, type: 'removed' }],
      new: [{ text: newText, type: 'added' }],
    };
  }

  const m = wordsA.length;
  const n = wordsB.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (wordsA[i - 1] === wordsB[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const oldParts: { text: string; type: 'same' | 'removed' }[] = [];
  const newParts: { text: string; type: 'same' | 'added' }[] = [];

  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && wordsA[i - 1] === wordsB[j - 1]) {
      oldParts.push({ text: wordsA[i - 1], type: 'same' });
      newParts.push({ text: wordsB[j - 1], type: 'same' });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      newParts.push({ text: wordsB[j - 1], type: 'added' });
      j--;
    } else {
      oldParts.push({ text: wordsA[i - 1], type: 'removed' });
      i--;
    }
  }

  oldParts.reverse();
  newParts.reverse();

  const merge = <T extends 'same' | 'removed' | 'added'>(
    parts: { text: string; type: T }[],
  ): { text: string; type: T }[] => {
    const merged: { text: string; type: T }[] = [];
    for (const p of parts) {
      const last = merged[merged.length - 1];
      if (last && last.type === p.type) { last.text += p.text; } else { merged.push({ ...p }); }
    }
    return merged;
  };

  return { old: merge(oldParts), new: merge(newParts) };
}

/* ── Status badge ─────────────────────────────────────────── */

function StatusBadge({ status }: { status: CompareClausePair['status'] }) {
  const cls = `cv-status-badge cv-status-badge--${status}`;
  const label = {
    changed:   '✏️ Changed',
    added:     '➕ Added',
    removed:   '➖ Removed',
    unchanged: '─ Unchanged',
  }[status];
  return <span className={cls}>{label}</span>;
}

/* ── Clause Card (D1: single-side card in the two-column layout) ── */

interface ClauseCardProps {
  pair: CompareClausePair;
  side: 'a' | 'b';
  /** Pre-computed diff — only for 'changed' pairs */
  diff: ReturnType<typeof diffWords> | null;
}

function ClauseCard({ pair, side, diff }: ClauseCardProps) {
  const isA = side === 'a';
  const body = isA ? pair.body_a : pair.body_b;
  const title = isA ? (pair.title_a || pair.title_b) : (pair.title_b || pair.title_a);

  // Determine card accent class
  const accentClass = pair.status === 'unchanged'
    ? 'cv-card--unchanged'
    : pair.status === 'changed'
      ? 'cv-card--changed'
      : pair.status === 'added'
        ? isA ? 'cv-card--ghost' : 'cv-card--added'
        : isA ? 'cv-card--removed' : 'cv-card--ghost';

  const isEmpty = isA
    ? pair.status === 'added'      // no A content for added pairs
    : pair.status === 'removed';   // no B content for removed pairs

  return (
    <div className={`cv-card ${accentClass}`}>
      {/* Card header: clause + status */}
      <div className="cv-card-header">
        <span className="cv-card-clause">§{pair.clause_number}</span>
        {title && <span className="cv-card-title">{title}</span>}
        <StatusBadge status={pair.status} />
      </div>

      {/* Card body: requirement text */}
      <div className="cv-card-body">
        {isEmpty ? (
          <span className="cv-card-empty">
            {isA ? '— not present in this edition —' : '— removed in this edition —'}
          </span>
        ) : pair.status === 'changed' && diff ? (
          /* Word-level diff highlighting */
          <p className="cv-card-text">
            {(isA ? diff.old : diff.new).map((part, idx) => (
              <span
                key={idx}
                className={
                  part.type === 'removed'
                    ? 'cv-text-removed'
                    : part.type === 'added'
                      ? 'cv-text-added'
                      : undefined
                }
              >
                {part.text}
              </span>
            ))}
          </p>
        ) : (
          <p className="cv-card-text">{body || '—'}</p>
        )}
      </div>
    </div>
  );
}

/* ── Main CompareView ─────────────────────────────────────── */

interface Props {
  onClose: () => void;
  /** Pre-fill standard_a */
  initialStandardA?: string;
  /** Pre-fill standard_b */
  initialStandardB?: string;
}

export default function CompareView({ onClose, initialStandardA, initialStandardB }: Props) {
  const [editions, setEditions] = useState<EditionInfo[]>([]);
  const [editionsLoading, setEditionsLoading] = useState(true);

  const [standardA, setStandardA] = useState(initialStandardA || '');
  const [standardB, setStandardB] = useState(initialStandardB || '');
  const [topic, setTopic] = useState('');

  const [result, setResult] = useState<CompareResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filterStatus, setFilterStatus] = useState<CompareClausePair['status'] | 'all'>('all');

  const dialogRef = useRef<HTMLDivElement>(null);

  // Load available editions on mount
  useEffect(() => {
    fetchEditions()
      .then(setEditions)
      .catch(() => setEditions([]))
      .finally(() => setEditionsLoading(false));
  }, []);

  const [selectedBase, setSelectedBase] = useState('');

  // Group editions by base standard
  const standardGroups = useMemo(() => {
    const groups = new Map<string, string[]>();
    for (const e of editions) {
      const base = e.standard
        .replace(/:\d{4}.*$/, '')
        .replace(/\s+\d{4}$/, '')
        .replace(/\s+Amd\s+\d+.*$/i, '')
        .trim();
      if (!groups.has(base)) groups.set(base, []);
      if (!groups.get(base)!.includes(e.standard)) groups.get(base)!.push(e.standard);
    }
    return new Map([...groups].filter(([, v]) => v.length >= 2).sort(([a], [b]) => a.localeCompare(b)));
  }, [editions]);

  const availableEditions = useMemo(
    () => standardGroups.get(selectedBase) ?? [],
    [standardGroups, selectedBase],
  );

  useEffect(() => {
    setStandardA('');
    setStandardB('');
  }, [selectedBase]);

  const handleCompare = useCallback(async () => {
    if (!standardA || !standardB) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await fetchCompare(standardA, standardB, topic || undefined);
      setResult(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Comparison failed');
    } finally {
      setLoading(false);
    }
  }, [standardA, standardB, topic]);

  // Filtered pairs + pre-computed diffs
  const filteredPairs = useMemo(() => {
    if (!result) return [];
    const pairs = filterStatus === 'all' ? result.pairs : result.pairs.filter(p => p.status === filterStatus);
    return pairs;
  }, [result, filterStatus]);

  const diffs = useMemo(() => {
    const map = new Map<string, ReturnType<typeof diffWords>>();
    for (const pair of filteredPairs) {
      if (pair.status === 'changed') {
        map.set(pair.clause_number, diffWords(pair.body_a, pair.body_b));
      }
    }
    return map;
  }, [filteredPairs]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) onClose();
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handler); };
  }, [onClose]);

  return (
    <div className="cv-overlay" role="dialog" aria-label="Compare standards">
      <div className="cv-dialog cv-dialog--wide" ref={dialogRef}>

        {/* ── Header ── */}
        <div className="cv-header">
          <h2 className="cv-title">⚖️ Compare Standards</h2>
          <button className="cv-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* ── Selection Form ── */}
        <div className="cv-form">
          <div className="cv-form-row">
            <label className="cv-form-label" style={{ flex: '1 1 100%' }}>
              <span>Standard</span>
              {editionsLoading ? (
                <input className="cv-form-input" placeholder="Loading editions…" disabled />
              ) : (
                <select
                  className="cv-form-select"
                  value={selectedBase}
                  onChange={e => setSelectedBase(e.target.value)}
                >
                  <option value="">Select a standard to compare editions…</option>
                  {[...standardGroups.keys()].map(base => (
                    <option key={base} value={base}>
                      {base} ({standardGroups.get(base)!.length} editions)
                    </option>
                  ))}
                </select>
              )}
            </label>
          </div>

          {selectedBase && (
            <div className="cv-form-row">
              <label className="cv-form-label">
                <span>Edition A (older / AU)</span>
                <select
                  className="cv-form-select"
                  value={standardA}
                  onChange={e => setStandardA(e.target.value)}
                >
                  <option value="">Select edition…</option>
                  {availableEditions.filter(s => s !== standardB).map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label className="cv-form-label">
                <span>Edition B (newer / International)</span>
                <select
                  className="cv-form-select"
                  value={standardB}
                  onChange={e => setStandardB(e.target.value)}
                >
                  <option value="">Select edition…</option>
                  {availableEditions.filter(s => s !== standardA).map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
            </div>
          )}

          <div className="cv-form-row">
            <label className="cv-form-label cv-form-label--topic">
              <span>Topic filter <span className="cv-form-optional">(optional)</span></span>
              <input
                className="cv-form-input"
                type="text"
                value={topic}
                onChange={e => setTopic(e.target.value)}
                placeholder="e.g. fire resistance, RCD protection…"
                onKeyDown={e => { if (e.key === 'Enter') handleCompare(); }}
              />
            </label>
            <button
              className="cv-compare-btn"
              onClick={handleCompare}
              disabled={loading || !standardA || !standardB}
            >
              {loading ? 'Comparing…' : 'Compare'}
            </button>
          </div>
        </div>

        {/* ── Error ── */}
        {error && <div className="cv-error">⚠️ {error}</div>}

        {/* ── Loading ── */}
        {loading && (
          <div className="cv-loading">
            <span className="corpus-loading-dot" />
            <span className="corpus-loading-dot" />
            <span className="corpus-loading-dot" />
            <span className="cv-loading-text">Comparing clauses…</span>
          </div>
        )}

        {/* ── Results: two-column card layout ── */}
        {result && !loading && (
          <div className="cv-results cv-results--sbs">

            {/* Summary bar */}
            <div className="cv-summary">
              <div className="cv-summary-title">
                {result.standard_a}
                <span className="cv-summary-arrow"> → </span>
                {result.standard_b}
                {result.topic && <span className="cv-summary-topic">({result.topic})</span>}
              </div>
              <div className="cv-summary-stats">
                <button
                  className={`cv-stat-chip cv-stat-chip--changed${filterStatus === 'changed' ? ' cv-stat-chip--active' : ''}`}
                  onClick={() => setFilterStatus(f => f === 'changed' ? 'all' : 'changed')}
                >
                  ✏️ {result.summary.changed} changed
                </button>
                <button
                  className={`cv-stat-chip cv-stat-chip--added${filterStatus === 'added' ? ' cv-stat-chip--active' : ''}`}
                  onClick={() => setFilterStatus(f => f === 'added' ? 'all' : 'added')}
                >
                  ➕ {result.summary.added} added
                </button>
                <button
                  className={`cv-stat-chip cv-stat-chip--removed${filterStatus === 'removed' ? ' cv-stat-chip--active' : ''}`}
                  onClick={() => setFilterStatus(f => f === 'removed' ? 'all' : 'removed')}
                >
                  ➖ {result.summary.removed} removed
                </button>
                <span className="cv-stat-total">{result.summary.total} total clauses</span>
              </div>
            </div>

            {/* AI Summary */}
            {result.ai_summary && (
              <div className="cv-ai-summary">
                <div className="cv-ai-summary-header">🤖 AI Summary</div>
                <div className="cv-ai-summary-body">{result.ai_summary}</div>
              </div>
            )}

            {/* Two-column card layout */}
            {filteredPairs.length === 0 ? (
              <div className="cv-pairs-empty">
                {filterStatus !== 'all'
                  ? `No ${filterStatus} clauses found. Try removing the filter.`
                  : 'No clause differences found between these editions.'}
              </div>
            ) : (
              <div className="cv-sbs-layout">
                {/* Column A header */}
                <div className="cv-sbs-col-header cv-sbs-col-header--a">
                  <span className="cv-sbs-standard-id">{result.standard_a}</span>
                  <span className="cv-sbs-edition-tag">Edition A</span>
                </div>
                {/* Divider header placeholder */}
                <div className="cv-sbs-col-header-divider" />
                {/* Column B header */}
                <div className="cv-sbs-col-header cv-sbs-col-header--b">
                  <span className="cv-sbs-standard-id">{result.standard_b}</span>
                  <span className="cv-sbs-edition-tag">Edition B</span>
                </div>

                {/* Column A — independently scrollable */}
                <div className="cv-sbs-col cv-sbs-col--a">
                  {filteredPairs.map(pair => (
                    <ClauseCard
                      key={pair.clause_number}
                      pair={pair}
                      side="a"
                      diff={diffs.get(pair.clause_number) ?? null}
                    />
                  ))}
                </div>

                {/* Vertical divider */}
                <div className="cv-sbs-divider" />

                {/* Column B — independently scrollable */}
                <div className="cv-sbs-col cv-sbs-col--b">
                  {filteredPairs.map(pair => (
                    <ClauseCard
                      key={pair.clause_number}
                      pair={pair}
                      side="b"
                      diff={diffs.get(pair.clause_number) ?? null}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
